import React from 'react';
import { Box, Container, Grid, Typography, Link, IconButton, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useThemeMode } from '../context/ThemeContext';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import contactInfo from '../data/contact';
import services from '../data/services';

const Footer = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { mode } = useThemeMode();

    const logoSrc = mode === 'dark'
        ? '/simplitech_logo_transparent.svg'
        : '/simplitech_logo_transparent_black_border.svg';

    const currentYear = new Date().getFullYear();

    // Softer red colors for footer
    const softRed = '#D6456A';
    const softRedLighter = '#F4B8C8';
    const softRedDark = '#741028c4';

    return (
        <Box
            component="footer"
            sx={{
                background: mode === 'dark'
                    ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${softRedDark} 100%)`
                    : `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${softRedLighter} 100%)`,
                color: theme.palette.text.secondary,
                py: 6,
                mt: 'auto',
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Company Info */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ mb: 3 }}>
                            <img
                                src={logoSrc}
                                alt="SimpliTechs Logo"
                                style={{
                                    height: isMobile ? 40 : 60,
                                    marginBottom: 16
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 2,
                                    lineHeight: 1.6,
                                    color: theme.palette.text.secondary
                                }}
                            >
                                SimpliTechs is a leading digital services agency specializing in
                                innovative solutions for modern businesses. We transform ideas into
                                powerful digital experiences.
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Quick Links */}
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                color: theme.palette.text.primary,
                                fontWeight: 600
                            }}
                        >
                            Quick Links
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Link
                                component={RouterLink}
                                to="/"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    textDecoration: 'none',
                                    '&:hover': { color: softRed }
                                }}
                            >
                                Home
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/about"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    textDecoration: 'none',
                                    '&:hover': { color: softRed }
                                }}
                            >
                                About
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/services"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    textDecoration: 'none',
                                    '&:hover': { color: softRed }
                                }}
                            >
                                Services
                            </Link>
                            <Link
                                component={RouterLink}
                                to="/contact"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    textDecoration: 'none',
                                    '&:hover': { color: softRed }
                                }}
                            >
                                Contact
                            </Link>
                        </Box>
                    </Grid>

                    {/* Services */}
                    <Grid item xs={12} sm={6} md={2}>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                color: theme.palette.text.primary,
                                fontWeight: 600
                            }}
                        >
                            Services
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {services.map((service) => (
                                <Link
                                    key={service.id}
                                    component={RouterLink}
                                    to={`/services`}
                                    sx={{ color: theme.palette.text.secondary, textDecoration: 'none', '&:hover': { color: softRed } }}
                                >
                                    {service.title}
                                </Link>
                            ))}
                        </Box>
                    </Grid>

                    {/* Contact Info */}
                    <Grid item xs={12} md={4}>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                color: theme.palette.text.primary,
                                fontWeight: 600
                            }}
                        >
                            Contact Info
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon sx={{ color: softRed, fontSize: 20 }} />
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    {contactInfo.emails[0]}
                                </Typography>
                            </Box>
                            {contactInfo.phones.map((phone, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PhoneIcon sx={{ color: softRed, fontSize: 20 }} />
                                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                        {phone.number} ({phone.label})
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Social Media Links */}
                        <Box sx={{ mt: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    mb: 2,
                                    color: theme.palette.text.primary,
                                    fontWeight: 600
                                }}
                            >
                                Follow Us
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                    href="#"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': { color: softRed }
                                    }}
                                >
                                    <FacebookIcon />
                                </IconButton>
                                <IconButton
                                    href="#"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': { color: softRed }
                                    }}
                                >
                                    <TwitterIcon />
                                </IconButton>
                                <IconButton
                                    href="#"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': { color: softRed }
                                    }}
                                >
                                    <LinkedInIcon />
                                </IconButton>
                                <IconButton
                                    href="#"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        '&:hover': { color: softRed }
                                    }}
                                >
                                    <InstagramIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Divider */}
                <Divider
                    sx={{
                        my: 4,
                        borderColor: theme.palette.divider
                    }}
                />

                {/* Copyright */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: 2
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                    >
                        © {currentYear} SimpliTechs. All rights reserved.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Link
                            href="#"
                            sx={{
                                color: theme.palette.text.secondary,
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: softRed }
                            }}
                        >
                            Privacy Policy
                        </Link>
                        <Link
                            href="#"
                            sx={{
                                color: theme.palette.text.secondary,
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                '&:hover': { color: softRed }
                            }}
                        >
                            Terms of Service
                        </Link>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer; 